/**
 * User Form Modal
 *
 * Modal for creating a new user with email invitation.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { UserRole, createUserSchema } from '@bstock/shared';
import type { CreateUserInput } from '@bstock/shared';
import { Product } from '@bill/core-types';
import { Modal, ModalFooter, Button, Input, Select } from '@/components/ui';
import { useCreateUser } from '../hooks/useUsers';
import { useAuth } from '@/context/AuthContext';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const roleOptions = [
  { value: UserRole.VIEWER, label: 'Consultation (VIEWER)' },
  { value: UserRole.OPERATOR, label: 'Operateur (OPERATOR)' },
  { value: UserRole.MANAGER, label: 'Responsable (MANAGER)' },
  { value: UserRole.ADMIN, label: 'Administrateur (ADMIN)' },
  { value: UserRole.OWNER, label: 'Proprietaire (OWNER)' },
];

export function UserFormModal({ isOpen, onClose }: UserFormModalProps) {
  const { user } = useAuth();
  const createMutation = useCreateUser();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateUserInput>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      role: UserRole.OPERATOR,
    },
  });

  // Filter role options based on current user's role
  // ADMIN cannot create OWNER
  const availableRoles = roleOptions.filter((option) => {
    if ((user?.currentBrand.bstockRole ?? user?.currentBrand.role) === UserRole.OWNER) {
      return true; // OWNER can create any role
    }
    // ADMIN cannot create OWNER
    return option.value !== UserRole.OWNER;
  });

  const selectedRole = watch('role');

  const onSubmit = async (data: CreateUserInput) => {
    try {
      // US-MAC.8 : creation depuis BStock → seul le bstockRole est pose,
      // les acces BStaff/BDisplay restent "Aucun acces" par defaut.
      await createMutation.mutateAsync({ ...data, module: Product.BSTOCK });
      reset();
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nouvel utilisateur"
      description="L'utilisateur recevra un email pour activer son compte."
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-dark-500 mb-1">
            Email *
          </label>
          <Input
            type="email"
            {...register('email')}
            placeholder="utilisateur@example.com"
            error={errors.email?.message}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-dark-500 mb-1">
              Prenom *
            </label>
            <Input
              {...register('firstName')}
              placeholder="Jean"
              error={errors.firstName?.message}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-500 mb-1">
              Nom *
            </label>
            <Input
              {...register('lastName')}
              placeholder="Dupont"
              error={errors.lastName?.message}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-dark-500 mb-1">
            Role *
          </label>
          <Select
            value={selectedRole}
            onChange={(value) => setValue('role', value as UserRole)}
            options={availableRoles}
            error={errors.role?.message}
          />
        </div>

        <ModalFooter className="pt-4">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting || createMutation.isPending}
          >
            Creer et inviter
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
